import { Navigate, useLocation } from "react-router-dom";
import { canPerform } from "@headerhawk/core/roles";
import { isImpersonationTab } from "@/lib/auth/impersonation";
import { useAuthSession, useAuthUser } from "./auth-context";
import { useProvisioning } from "@/lib/hooks/use-provisioning";
import { roleFromProfile } from "@/lib/hooks/use-current-role";
import { useSites } from "@/lib/hooks/use-sites";
import { useReturningUser } from "@/lib/hooks/use-returning-user";
import { ApiError } from "@/lib/api/client";
import { FullScreenLoader } from "@/components/ui/loader";
import { NoSitesYet } from "./no-sites-yet";

interface RequireAuthProps {
  children: React.ReactNode;
}

export function RequireAuth({ children }: RequireAuthProps) {
  const { isAuthenticated, isSessionLoading } = useAuthSession();
  const { isUserLoading } = useAuthUser();
  const { isProvisioned, isProvisioning, provisioningError, profile } =
    useProvisioning();
  const { isLikelyReturningUser } = useReturningUser();
  const {
    data: sitesData,
    isLoading: isSitesLoading,
    error: sitesError,
    refetch: refetchSites,
  } = useSites({
    enabled: isLikelyReturningUser || isProvisioned,
  });
  const location = useLocation();
  // Carry the query string too - a filtered view is worth returning to.
  const from = `${location.pathname}${location.search}`;

  // Hard gate: auth failure (only after loading completes)
  if (!isSessionLoading && !isUserLoading && !isAuthenticated) {
    return <Navigate to="/login" state={{ from }} replace />;
  }

  // Hard gate: 401 from sites or provisioning means session is invalid
  const sitesStatus = (sitesError as ApiError | null)?.status;
  const provisioningStatus = (provisioningError as ApiError | null)?.status;

  if (sitesStatus === 401 || provisioningStatus === 401) {
    return <Navigate to="/login" state={{ from }} replace />;
  }

  // Hard gate: non-401 provisioning error
  if (provisioningError && provisioningStatus !== 401) {
    return (
      <div className="flex min-h-screen flex-col items-center justify-center gap-4">
        <div className="text-destructive">Failed to set up your account</div>
        <div className="text-sm text-muted-foreground">
          {provisioningError.message}
        </div>
        <button
          onClick={() => window.location.reload()}
          className="text-sm text-primary underline"
        >
          Try again
        </button>
      </div>
    );
  }

  // No sites. Onboarding is where a tenant's creator names the organization,
  // picks a plan and adds the first site, and naming the organization is
  // owner-only (`POST /tenants/setup`), with the plan admin-only after it.
  // Someone who joined an existing tenant - invited, or left behind when its
  // last site was deleted - would be walked into steps the API refuses, so
  // they get a page saying what is missing instead. Choosing needs the role,
  // so this waits for provisioning.
  const withoutSites = () => {
    if (!isProvisioned) {
      return <FullScreenLoader />;
    }
    // Onboarding is nothing but writes, so an operator's read-only view of an
    // owner (task 394) gets the page that says what is missing instead.
    if (
      !isImpersonationTab() &&
      canPerform(roleFromProfile(profile), "POST /tenants/setup")
    ) {
      return <Navigate to="/onboarding" replace />;
    }
    return <NoSitesYet onSiteAdded={() => void refetchSites()} />;
  };

  // Hard gate: sites loaded but empty means onboarding incomplete
  if (!isSitesLoading && sitesData?.sites.length === 0) {
    return withoutSites();
  }

  // Returning users with sites confirmed: render children immediately,
  // skipping provisioning and remaining loading gates. The /sites query
  // fires early (enabled by isLikelyReturningUser) in parallel with
  // Descope auth refresh and provisioning. Once sites data arrives with
  // at least one site, we know the user is fully set up and can render.
  if (isLikelyReturningUser && sitesData && sitesData.sites.length > 0) {
    return <>{children}</>;
  }

  // First-time users: sequential loading gates (original behavior)
  if (isSessionLoading || isUserLoading) {
    return <FullScreenLoader />;
  }

  if (isProvisioning) {
    return <FullScreenLoader />;
  }

  if (!isProvisioned) {
    return <FullScreenLoader />;
  }

  if (isSitesLoading) {
    return <FullScreenLoader />;
  }

  const hasSites = sitesData?.sites && sitesData.sites.length > 0;
  if (!hasSites) {
    return withoutSites();
  }

  return <>{children}</>;
}
